import { Globe, Loader2, Trash2, X } from 'lucide-react';
import { CrawlAuthSection } from './crawl/CrawlAuthSection';
import { CrawlCompletionPanel } from './crawl/CrawlCompletionPanel';
import { CrawlForceRecrawlToggle } from './crawl/CrawlForceRecrawlToggle';
import { CrawlProgressPanel } from './crawl/CrawlProgressPanel';
import { CrawlRecursiveOptions } from './crawl/CrawlRecursiveOptions';
import { useCrawlExecution } from '../hooks/useCrawlExecution';

function ClearFieldButton({ visible, onClick }) {
  if (!visible) return null;
  return (
    <button type="button" onClick={onClick} tabIndex={-1}
      className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-600 transition-colors hover:text-red-400"
      aria-label="Effacer">
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function CrawlModalHeader({ crawling, onClose }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-serif tracking-tight">Crawler un site web</h3>
      <button
        type="button"
        data-testid="close-crawl-modal"
        onClick={onClose}
        disabled={crawling}
        className="rounded-sm p-2 transition-colors hover:bg-zinc-900/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function CrawlUrlFieldHeader({ isBatch, setIsBatch, crawling }) {
  return (
    <div className="flex items-center justify-between">
      <label htmlFor="url" className="text-sm font-medium">
        {isBatch ? 'Liste d\'URLs (une par ligne)' : 'URL du site'}
      </label>
      <button
        type="button"
        data-testid="toggle-batch-mode"
        onClick={() => setIsBatch(!isBatch)}
        disabled={crawling}
        className="text-xs text-zinc-400 underline transition-colors hover:text-white disabled:opacity-50"
      >
        {isBatch ? 'URL unique' : 'Liste d\'URLs'}
      </button>
    </div>
  );
}

function CrawlUrlListInput({ urlList, setUrlList, crawling }) {
  return (
    <div className="relative">
      <textarea
        id="url-list"
        data-testid="url-list-input"
        value={urlList}
        onChange={(event) => setUrlList(event.target.value)}
        placeholder={'https://exemple.com/page-1\nhttps://exemple.com/page-2\nhttps://exemple.com/page-3'}
        rows={6}
        className="w-full rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3 pr-8 text-sm text-white placeholder:text-zinc-600 transition-colors duration-300 focus:border-white focus:outline-none focus:ring-0"
        disabled={crawling}
      />
      <ClearFieldButton visible={urlList.length > 0} onClick={() => setUrlList('')} />
    </div>
  );
}

function CrawlUrlSingleInput({ url, setUrl, crawling }) {
  return (
    <div className="relative">
      <Globe className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-600" />
      <input
        id="url"
        type="url"
        data-testid="url-input"
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        placeholder="https://exemple.com"
        className="h-12 w-full rounded-sm border border-zinc-800 bg-zinc-900/50 pl-12 pr-8 text-white placeholder:text-zinc-600 transition-colors duration-300 focus:border-white focus:outline-none focus:ring-0"
        required
        disabled={crawling}
      />
      <ClearFieldButton visible={url.length > 0} onClick={() => setUrl('')} />
    </div>
  );
}

function CrawlUrlField({ url, setUrl, crawling, isBatch, urlList, setUrlList, setIsBatch }) {
  const urlCount = isBatch ? urlList.split('\n').filter((l) => l.trim().startsWith('http')).length : 0;
  return (
    <div className="space-y-2">
      <CrawlUrlFieldHeader isBatch={isBatch} setIsBatch={setIsBatch} crawling={crawling} />
      {isBatch
        ? <CrawlUrlListInput urlList={urlList} setUrlList={setUrlList} crawling={crawling} />
        : <CrawlUrlSingleInput url={url} setUrl={setUrl} crawling={crawling} />
      }
      {isBatch ? <p className="text-xs text-zinc-500">{urlCount} URL(s) détectée(s)</p> : null}
    </div>
  );
}

function CrawlFormActions({ crawling, onClose }) {
  return (
    <div className="flex gap-3">
      <button
        type="button"
        data-testid="cancel-crawl-button"
        onClick={onClose}
        disabled={crawling}
        className="h-12 flex-1 rounded-sm border border-zinc-800 bg-transparent text-white transition-all duration-300 hover:border-white hover:bg-zinc-900/50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Annuler
      </button>
      <button
        type="submit"
        data-testid="crawl-submit-button"
        disabled={crawling}
        className="flex h-12 flex-1 items-center justify-center gap-2 rounded-sm bg-white font-medium text-black transition-all duration-300 hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {crawling ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Crawling...</span>
          </>
        ) : (
          'Crawler'
        )}
      </button>
    </div>
  );
}

function CrawlAuthFields({ crawlState }) {
  return (
    <CrawlAuthSection
      authMode={crawlState.authMode}
      setAuthMode={crawlState.setAuthMode}
      username={crawlState.username}
      setUsername={crawlState.setUsername}
      password={crawlState.password}
      setPassword={crawlState.setPassword}
      googleEmail={crawlState.googleEmail}
      setGoogleEmail={crawlState.setGoogleEmail}
      googlePassword={crawlState.googlePassword}
      setGooglePassword={crawlState.setGooglePassword}
      sessionCookies={crawlState.sessionCookies}
      setSessionCookies={crawlState.setSessionCookies}
      rememberGoogleSession={crawlState.rememberGoogleSession}
      setRememberGoogleSession={crawlState.setRememberGoogleSession}
      useSavedGoogleSession={crawlState.useSavedGoogleSession}
      setUseSavedGoogleSession={crawlState.setUseSavedGoogleSession}
      showCookieHelp={crawlState.showCookieHelp}
      setShowCookieHelp={crawlState.setShowCookieHelp}
      crawling={crawlState.crawling}
    />
  );
}

function CrawlModalForm({ crawlState, onClose }) {
  return (
    <form onSubmit={crawlState.handleCrawl} className="space-y-4">
      <CrawlUrlField
        url={crawlState.url}
        setUrl={crawlState.setUrl}
        crawling={crawlState.crawling}
        isBatch={crawlState.isBatch}
        urlList={crawlState.urlList}
        setUrlList={crawlState.setUrlList}
        setIsBatch={crawlState.setIsBatch}
      />
      {!crawlState.isBatch ? (
        <CrawlRecursiveOptions
          isRecursive={crawlState.isRecursive}
          setIsRecursive={crawlState.setIsRecursive}
          maxPages={crawlState.maxPages}
          setMaxPages={crawlState.setMaxPages}
          urlPrefix={crawlState.urlPrefix}
          setUrlPrefix={crawlState.setUrlPrefix}
          crawling={crawlState.crawling}
        />
      ) : null}
      <CrawlForceRecrawlToggle
        forceRecrawl={crawlState.forceRecrawl}
        setForceRecrawl={crawlState.setForceRecrawl}
        crawling={crawlState.crawling}
      />
      <CrawlAuthFields crawlState={crawlState} />
      <p className="text-xs text-zinc-600">Le contenu des pages sera extrait et indexé dans la base de connaissances.</p>
      <CrawlFormActions crawling={crawlState.crawling} onClose={onClose} />
    </form>
  );
}

function CrawlModalBody({ crawlState }) {
  return (
    <div data-testid="crawl-modal-overlay" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={crawlState.handleClose}>
      <div data-testid="crawl-modal" className="max-h-[90vh] w-full max-w-[38.4rem] space-y-5 overflow-y-auto rounded-md border border-border bg-card p-6" onClick={(event) => event.stopPropagation()}>
        <CrawlModalHeader crawling={crawlState.crawling} onClose={crawlState.handleClose} />
        <CrawlCompletionPanel completedResult={crawlState.completedResult} onClose={crawlState.handleClose} />
        <CrawlProgressPanel crawling={crawlState.crawling} progress={crawlState.progress} progressPercentage={crawlState.progressPercentage} />
        {!crawlState.completedResult ? <CrawlModalForm crawlState={crawlState} onClose={crawlState.handleClose} /> : null}
      </div>
    </div>
  );
}

export default function CrawlModal({ knowledgeBaseId, onClose, onSuccess }) {
  const crawlState = useCrawlExecution({ knowledgeBaseId, onClose, onSuccess });
  return <CrawlModalBody crawlState={crawlState} />;
}
